import React from 'react';
import { Agreement, useCurrentAgreement, useCurrentUser, User } from '@economic/react-app';
import { Header } from '@economic/taco';
import { useNavigate } from 'react-router-dom';
import useCachedQuery from '../../shared/hooks/useCachedQuery';
import VismaConnectMissing2FaDialog from '../../shared/components/modals/VismaConnectMissing2FaDialog';
import useMutation from '../../shared/hooks/useMutation';
import VismaConnectAddAccountDialog from '../../shared/components/modals/VismaConnectAddAccountDialog';
import GlobalKeyboardShortcuts from '../../shared/components/GlobalKeyboardShortcuts';

const FALLBACK_IMAGE_SRC = `${process.env.PUBLIC_URL}/agreement/logo_default.svg`;

const sortByName = (a, b) => a.name.localeCompare(b.name);

function AgreementSelector() {
    const agreement = useCurrentAgreement();
    const user = useCurrentUser();
    const navigate = useNavigate();
    const agreementsQuery = useCachedQuery<any[]>('/agreementselector', []);
    const mutate = useMutation('/agreementselector/switch');
    const [open, setOpen] = React.useState(false);

    // search
    const filterAgreement = (agreement, search: (agreement) => boolean) => {
        return agreement.statuses.length === 0 && (!!agreement.clients?.length || search(agreement));
    };
    const filterClientAgreement = (agreement, searchValue: string, search: (agreement) => boolean) => {
        if (agreement.statuses.length === 0 && (agreement.favorite || searchValue.length >= 3)) {
            return search(agreement);
        }

        return false;
    };

    const agreements = React.useMemo(() => {
        const map = ag => ({
            adminAgreementNumber: ag.adminAgreementNumber,
            number: ag.agreementNumber,
            name: ag.companyName,
            userId: ag.userId,
            isAdministrator: ag.isAdministrator,
            isDeveloper: ag.isDeveloper,
            imageSrc: `${process.env.ECONOMIC_INTERNALAPI_PATH}/internal-api/agreements/logo/${ag.agreementNumber}`,
            statuses: ag.statuses,
            clients: ag.clients?.map(map).sort(sortByName),
            favorite: ag.favorite,
            secure: ag.hasCompany2FaEnforced && user.isLoggedInWithVismaConnect && !user.isLoggedInWith2Fa,
        });

        return agreementsQuery.data.map(map).sort(sortByName);
    }, [agreementsQuery.data]);

    // change agreement
    const [showMissing2Fa, setShowMissing2Fa] = React.useState(false);

    const handleChangeAgreement = async agreement => {
        if (agreement.hasCompany2FaEnforced && user.isLoggedInWithVismaConnect && !user.isLoggedInWith2Fa) {
            setShowMissing2Fa(true);
            return;
        }

        const response = await mutate({
            adminAgreementNumber: agreement.adminAgreementNumber,
            agreementNumber: agreement.number,
            companyName: agreement.name,
            userId: agreement.userId,
            favorite: agreement.favorite,
            isAdministrator: agreement.isAdministrator,
            isDeveloper: agreement.isDeveloper,
            statuses: agreement.statuses,
        });

        if (response?.data.redirectUrl) {
            window.location.href = response.data.redirectUrl;
        } else {
            window.location.href = window.location.origin;
        }
    };

    // add agreement
    const [showAddAgreement, setShowAddAgreement] = React.useState(false);
    const handleAddAgreementSuccess = () => agreementsQuery.refetch();
    const handleAddAgreement = user.vismaId ? () => setShowAddAgreement(true) : undefined;

    const handleShortcut = event => {
        event.preventDefault();
        setOpen(open => !open);
    };

    return (
        <>
            <GlobalKeyboardShortcuts handlers={{ ',': handleShortcut }}>
                <Header.AgreementSelector
                    agreements={agreements}
                    currentAgreement={mapCurrentAgreement(agreement, user)}
                    filterAgreement={filterAgreement}
                    filterClientAgreement={filterClientAgreement}
                    fallbackImageSrc={FALLBACK_IMAGE_SRC}
                    onAddAgreement={handleAddAgreement}
                    onChangeAgreement={handleChangeAgreement}
                    onLogout={() => navigate('/logout')}
                    open={open}
                    setOpen={setOpen}
                />
            </GlobalKeyboardShortcuts>
            {showAddAgreement ? (
                <VismaConnectAddAccountDialog
                    onOk={handleAddAgreementSuccess}
                    open={showAddAgreement}
                    setOpen={setShowAddAgreement}
                />
            ) : null}
            {showMissing2Fa ? (
                <VismaConnectMissing2FaDialog
                    onOk={() => setShowMissing2Fa(false)}
                    open={showMissing2Fa}
                    setOpen={setShowMissing2Fa}
                />
            ) : null}
        </>
    );
}

export function AgreementDisplay() {
    const agreement = useCurrentAgreement();
    const user = useCurrentUser();

    if (user.vismaId || user.isAdministrator) {
        return <AgreementSelector />;
    }

    return (
        <Header.AgreementDisplay currentAgreement={mapCurrentAgreement(agreement, user)} fallbackImageSrc={FALLBACK_IMAGE_SRC} />
    );
}

const mapCurrentAgreement = (agreement: Agreement, user: User) => ({
    number: agreement.number,
    name: agreement.name,
    userId: user.id,
    isAdministrator: user.isAdministrator,
    isDeveloper: agreement.isDeveloper,
    imageSrc: `${process.env.ECONOMIC_INTERNALAPI_PATH}/internal-api/agreements/logo/${agreement.number}`,
    statuses: [],
    secure: false,
});
